import { type GradingSubmission, GradingSubmissionSchema } from "./gradingSubmission";

// A normalized view of a grading result, regardless of whether it arrived via
// Ably (human submission = GradingSubmission; resolver fallback = flat shape)
// or HTTP polling (flat responses keyed by questionId).
export type GradingOutcome = {
	responses: Record<string, unknown>;
	alerts: Record<string, string[]>;
	errors?: string[];
	phonePositionAnswer?: string;
};

export const hasAlerts = (outcome: GradingOutcome): boolean => Object.keys(outcome.alerts).length > 0;

// A grading the grader could not produce reports its reason here and is invisible to
// `hasAlerts`. A human grader can also answer the console's alert question, and the polling
// fallback flattens that answer into the same field — so only a grading that produced no
// answers at all is the resolver reporting it could not grade the device.
export const isFailedGrading = (outcome: GradingOutcome): boolean =>
	Boolean(outcome.errors?.length) && Object.keys(outcome.responses).length === 0;

const METADATA_KEYS = new Set(["alerts", "errors", "isValidPhonePosition"]);

const fromSubmission = (sub: GradingSubmission): GradingOutcome => {
	const responses: Record<string, unknown> = {};
	let phonePositionAnswer: string | undefined;
	for (const { questionId, response } of sub.results) {
		if (questionId === "phonePositionAnswer") {
			phonePositionAnswer = response as string | undefined;
			continue;
		}
		if (METADATA_KEYS.has(questionId)) continue;
		responses[questionId] = response;
	}
	return { responses, alerts: sub.alerts, phonePositionAnswer };
};

// Legacy shapes come from two places:
// - resolver output published directly by the grader server (AI/auto-responder/cache):
//     { ...flatQuestions, alerts?, isValidPhonePosition?, errors?, phonePositionAnswer? }
// - the polling endpoint's `results` field after transformResults flattening:
//     { ...flatQuestions }  (no metadata)
const fromLegacyFlat = (raw: Record<string, unknown>): GradingOutcome => {
	const { alerts, errors, phonePositionAnswer, isValidPhonePosition: _ignored, ...responses } = raw;
	return {
		responses,
		alerts: (alerts as Record<string, string[]> | undefined) ?? {},
		errors: errors as string[] | undefined,
		phonePositionAnswer: phonePositionAnswer as string | undefined,
	};
};

export const parseAblyGradingMessage = (raw: unknown): GradingOutcome | null => {
	if (!raw || typeof raw !== "object") return null;
	const parsedSubmission = GradingSubmissionSchema.safeParse(raw);
	if (parsedSubmission.success) return fromSubmission(parsedSubmission.data);
	return fromLegacyFlat(raw as Record<string, unknown>);
};

export const outcomeFromPollingResults = (results: unknown): GradingOutcome => {
	if (!results || typeof results !== "object") return { responses: {}, alerts: {} };
	return fromLegacyFlat(results as Record<string, unknown>);
};

export const outcomeFromAlerts = (alerts: Record<string, string[]>): GradingOutcome => ({
	responses: {},
	alerts,
});

// A grading that failed outright says nothing about which angle was at fault, so every
// expected angle is asked for again.
export const retakeAllAngles = (angles: readonly string[]): GradingOutcome =>
	outcomeFromAlerts(Object.fromEntries(angles.map((angle) => [angle, ["unexpected-issues"]])));
